import { Stack, useMantineTheme } from '@mantine/core';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IAggregatedStats } from '@feathr/blackbox';
import { calcDelta, Skeleton } from '@feathr/components';

import type { TAttributionModel } from '../ReportsConversionsDashboardPage.utils';
import DeltaIndicator from './DeltaIndicator';
import { getAttrStats } from './KpiTiles.utils';
import TileError from './TileError';

import * as styles from './KpiTiles.css';

export interface ITotalConversionsTileProps {
  attr: TAttributionModel;
  currentStats?: IAggregatedStats;
  hasError: boolean;
  isLoading: boolean;
  priorStats?: IAggregatedStats;
}

function TotalConversionsTile({
  attr,
  currentStats,
  hasError,
  isLoading,
  priorStats,
}: Readonly<ITotalConversionsTileProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const currentNum = getAttrStats(currentStats, attr)?.num ?? 0;
  const priorNum = getAttrStats(priorStats, attr)?.num ?? 0;
  const delta = calcDelta(currentNum, priorNum);
  const uniqueConverters = currentStats?.num_persons_converted ?? 0;

  if (hasError) {
    return <TileError label={t('Total Conversions')} />;
  }

  return (
    <Stack gap={theme.spacing['2']}>
      <span className={styles.label}>{t('Total Conversions')}</span>
      {isLoading ? (
        <>
          <Skeleton height={36} width={120} />
          <Skeleton height={16} width={180} />
          <Skeleton height={40} width={100} />
        </>
      ) : (
        <>
          <span className={styles.primaryStat}>{numeral(currentNum).format('0,0')}</span>
          <DeltaIndicator delta={delta} />
          <Stack gap={theme.spacing['1']}>
            <span className={styles.secondaryLabel}>{t('Unique Converters')}</span>
            <span className={styles.secondaryStat}>{numeral(uniqueConverters).format('0,0')}</span>
          </Stack>
        </>
      )}
    </Stack>
  );
}

export default TotalConversionsTile;
